import { ConfirmationModal, Text, useModal } from '@procurify/ui'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type PODisclaimerLabel } from '@webapp/modules/purchasing/entities/PODisclaimerLabel'
import { useMutateDeletePODisclaimerLabel } from '@webapp/modules/purchasing/features/delete-po-disclaimer-label/models'
import messages from './messages'

interface UseDeletePODisclaimerLabelPresenterOptions {
  onSuccess?: () => void
}

/**
 * Danger confirmation + DELETE for one PO disclaimer label. The programmatic
 * `ConfirmationModal` replaces the Angular hold-to-delete anti-pattern
 * (BHV-025); the row disappears via list refetch, not a local splice.
 */
export const useDeletePODisclaimerLabelPresenter = (
  options: UseDeletePODisclaimerLabelPresenterOptions = {}
) => {
  const intl = useProcurifyIntl()
  const modal = useModal()
  const { mutateAsync, isLoading } = useMutateDeletePODisclaimerLabel()

  const deleteLabel = (label: PODisclaimerLabel) => {
    modal.showModal(ConfirmationModal, {
      open: true,
      icon: 'attention',
      type: 'danger',
      header: intl.formatMessage(messages.confirmationModalTitle),
      content: (
        <Text>
          {intl.formatMessage(messages.confirmationModalDescription, {
            description: (
              <Text bold inline>
                {label.description}
              </Text>
            ),
          })}
        </Text>
      ),
      confirmButtonText: intl.formatMessage(
        messages.confirmationModalConfirmButton
      ),
      cancelButtonText: intl.formatMessage(
        messages.confirmationModalCancelButton
      ),
      handleDismiss: () => {
        // Cancel makes no API call (BHV-025)
        modal.hideModal()
      },
      onConfirm: async (done: () => void) => {
        try {
          await mutateAsync(label.id)

          toast.success({
            title: intl.formatMessage(messages.successTitle),
            children: intl.formatMessage(messages.successMessage),
          })

          modal.hideModal()
          options.onSuccess?.()
        } catch (error) {
          const errorMessage =
            getMessageFromError(error) ||
            intl.formatMessage(messages.defaultErrorMessage)

          toast.error({
            title: intl.formatMessage(messages.errorTitle),
            children: errorMessage,
          })

          // Keep the modal open/retryable on failure
          done()
        }
      },
    })
  }

  return {
    commands: {
      deleteLabel,
    },
    states: {
      isDeleting: isLoading,
    },
  }
}
